// components/ThresholdModal.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Table, InputNumber, Button, Form, message, Typography, Space, Popconfirm } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { fetchAllThresholds, updateThreshold } from '../api';

const { Text } = Typography;

const ThresholdModal = ({ open, onClose, activeTab }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [form] = Form.useForm();

    const loadThresholds = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await fetchAllThresholds();
            setData(rows);
        } catch (err) {
            message.error(err.message || 'Failed to load thresholds');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) loadThresholds();
    }, [open, loadThresholds]);

    const isEditing = (record) => record.type_slug === editingKey;

    const startEdit = (record) => {
        form.setFieldsValue({ threshold_kg: record.threshold_kg });
        setEditingKey(record.type_slug);
    };

    const cancelEdit = () => setEditingKey(null);

    const save = async (typeSlug) => {
        try {
            const { threshold_kg } = await form.validateFields();
            await updateThreshold(typeSlug, threshold_kg);
            message.success(`Threshold for "${typeSlug}" updated to ${threshold_kg}kg`);
            setEditingKey(null);
            loadThresholds();
        } catch (err) {
            if (err?.errorFields) return; // form validation error, already shown inline
            message.error(err.message || 'Failed to update threshold');
        }
    };

    const columns = [
        {
            title: 'Type',
            dataIndex: 'type_slug',
            render: (val) => (
                <Text strong={val === activeTab?.toLowerCase()}>
                    {val}
                    {val === activeTab?.toLowerCase() && (
                        <Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal' }}>
                            (current tab)
                        </Text>
                    )}
                </Text>
            )
        },
        {
            title: 'Market Weight Threshold (kg)',
            dataIndex: 'threshold_kg',
            render: (val, record) =>
                isEditing(record) ? (
                    <Form form={form} component={false}>
                        <Form.Item
                            name="threshold_kg"
                            style={{ margin: 0 }}
                            rules={[
                                { required: true, message: 'Required' },
                                {
                                    validator: (_, value) =>
                                        value > 0
                                            ? Promise.resolve()
                                            : Promise.reject(new Error('Must be greater than 0'))
                                }
                            ]}
                        >
                            <InputNumber min={0.1} step={1} style={{ width: 140 }} autoFocus />
                        </Form.Item>
                    </Form>
                ) : (
                    `${Number(val).toLocaleString()} kg`
                )
        },
        {
            title: 'Last Updated',
            dataIndex: 'updated_at',
            render: (val) => (val ? new Date(val).toLocaleString() : '—')
        },
        {
            title: 'Actions',
            render: (_, record) =>
                isEditing(record) ? (
                    <Space>
                        <Button
                            type="primary"
                            size="small"
                            icon={<SaveOutlined />}
                            onClick={() => save(record.type_slug)}
                        >
                            Save
                        </Button>
                        <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit}>
                            Cancel
                        </Button>
                    </Space>
                ) : (
                    <Popconfirm
                        title="Edit this threshold?"
                        description="This changes market-readiness for all animals of this type."
                        onConfirm={() => startEdit(record)}
                        okText="Edit"
                        cancelText="Cancel"
                        disabled={editingKey !== null}
                    >
                        <Button size="small" icon={<EditOutlined />} disabled={editingKey !== null}>
                            Edit
                        </Button>
                    </Popconfirm>
                )
        }
    ];

    return (
        <Modal
            title="Manage Market Weight Thresholds"
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>
            ]}
            width={720}
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Animals reach <Text strong>"Ready for Market"</Text> status once their current weight
                meets or exceeds the threshold set for their type. Click <Text strong>Edit</Text> on a
                row to update it — changes apply immediately.
            </Text>
            <Table
                rowKey="type_slug"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={false}
                size="small"
            />
        </Modal>
    );
};

export default ThresholdModal;